import type { SectionQuestions as Questions, SectionsState } from '@bk/contracts';
import { useState } from 'react';
import { errorMessage } from '../../lib/api';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Button, ErrorText } from '../../ui';
import { answerIdentity } from '../../lib/condition-answers';
import { useConditionAnswers } from './ConditionAnswersSync';

type Question = Questions['domande'][number];

/**
 * Le domande rimandate in questa visita, di tutte le sezioni aperte: «Più tardi» passa alla domanda dopo, e le rimandate
 * si sommano nel pulsante in basso per riprenderle quando si vuole (o alla prossima apertura dell'app).
 */
const later = new Map<string, Question>();
/** Ripresa delle rimandate dal pulsante in basso: quelle saltate di nuovo con «Più tardi» in questo giro. */
let reviewing = false;
const skipped = new Set<string>();

/** Anche cambiando sezione, le ricomposizioni del gestionale devono finire una alla volta. */
let queue: Promise<void> = Promise.resolve();

/**
 * Le domande di una sezione del gestionale (ADR 0195 § 7, decisione del 2026-10-27): all'iscrizione nessuna domanda e
 * l'ipotesi più ampia; quando il titolare apre la sezione, un pop-up chiede solo le condizioni da confermare che la
 * governano. Un «no» toglie le voci e i documenti della condizione, un «sì» le conferma, in tutte le sezioni che governa.
 */
export function SectionQuestions({ sezione }: { sezione: string | null | undefined }) {
  const { tenant, isManager, livello } = useTenant();
  const { outbox, hidden } = useConditionAnswers();
  const key = sezione ?? '';
  // Dentro una sede le domande e le risposte sono di quella sede (ADR 0210 § 5).
  const q = useTenantQuery<Questions>(['conditions', 'sezione', key, livello ?? 'azienda'], `/conditions/sezione/${encodeURIComponent(key)}${livello ? `?sede=${livello}` : ''}`, { enabled: !!sezione && isManager });
  const [error, setError] = useState<string | null>(null);
  const [, setClosed] = useState(0);
  const refresh = () => setClosed((n) => n + 1);
  const scope = `${tenant.id}:${livello ?? 'azienda'}:`;
  const isAnswered = (q: Question) => hidden.has(answerIdentity(q.chiave, livello ?? undefined));
  if (!isManager) return null;
  // Le rimandate di questa azienda e di questo livello, da tutte le sezioni.
  const rimandate = [...later].filter(([id, question]) => id.startsWith(scope) && !isAnswered(question)).map(([, x]) => x);
  // Le domande di questa sezione: prima quelle mai viste, le rimandate restano al pulsante.
  const domande = sezione ? (q.data?.domande ?? []).filter((x) => !isAnswered(x) && !later.has(scope + x.chiave)) : [];
  const daRivedere = reviewing ? rimandate.filter((x) => !skipped.has(scope + x.chiave)) : [];
  if (reviewing && !daRivedere.length) {
    reviewing = false;
    skipped.clear();
  }
  const elenco = daRivedere.length ? daRivedere : domande;
  if (!elenco.length) {
    if (!rimandate.length) return null;
    return (
      <button
        type="button"
        onClick={() => {
          reviewing = true;
          skipped.clear();
          refresh();
        }}
        className="fixed bottom-4 right-4 z-40 rounded-full bg-amber-100 px-4 py-2 text-sm font-medium text-amber-800 shadow-lg hover:bg-amber-200"
      >
        {rimandate.length === 1 ? '1 domanda da confermare' : `${rimandate.length} domande da confermare`} · Rispondi
      </button>
    );
  }
  const d = elenco[0]!;
  const id = scope + d.chiave;

  // La scrittura locale è sincrona; invio e ricomposizione non bloccano la domanda successiva.
  const answer = (stato: 'si' | 'no') => {
    try {
      outbox.answer(d.chiave, { stato, ...(livello ? { sede: livello } : {}) });
      later.delete(id);
      setError(null);
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  // «Più tardi» rimanda solo questa domanda e passa alla successiva; le rimandate si sommano nel pulsante in basso.
  const postpone = () => {
    if (reviewing) skipped.add(id);
    else later.set(id, d);
    setError(null);
    refresh();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" role="dialog" aria-modal="true" aria-labelledby="domanda-sezione">
      <div className="w-full max-w-md space-y-4 rounded-2xl bg-white p-5 shadow-xl">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-amber-700">
            {daRivedere.length ? 'Rimandate' : 'Da confermare'}
            {elenco.length > 1 ? ` · 1 di ${elenco.length}` : ''}
          </p>
          <p id="domanda-sezione" className="mt-1 text-sm font-semibold">
            {d.etichetta}?
          </p>
          {d.descrizione && <p className="mt-1 text-sm text-ink/65">{d.descrizione}</p>}
        </div>
        <p className="text-xs text-ink/55">
          {livello ? 'La risposta vale per questa sede. ' : ''}Per ora vale: la sezione è preparata come se fosse sì.
          {d.altre_sezioni > 0 && ` La risposta vale anche per ${d.altre_sezioni === 1 ? 'un’altra sezione' : `altre ${d.altre_sezioni} sezioni`}.`}
        </p>
        {error && <ErrorText>{error}</ErrorText>}
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="ghost" onClick={postpone}>
            Più tardi
          </Button>
          <Button variant="ghost" onClick={() => answer('no')}>
            No
          </Button>
          <Button onClick={() => answer('si')}>
            Sì
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Le sezioni del gestionale composto (ADR 0195 § 7): quali tipi e quali cartelle valgono per ipotesi o sono da raccogliere. */
export function useSections() {
  const { livello } = useTenant();
  const q = useTenantQuery<SectionsState>(['model', 'sections', livello ?? 'azienda'], `/model/sections${livello ? `?sede=${livello}` : ''}`);
  const tipi = new Map((q.data?.tipi ?? []).map((x) => [x.key, x]));
  const documenti = new Map((q.data?.documenti ?? []).map((x) => [x.id, x]));
  return { tipi, documenti, state: q.data };
}

/** Il segno di una sezione che vale per ipotesi: la conferma si dà aprendola. */
export function ToConfirm({ small = false }: { small?: boolean }) {
  return (
    <span title="Preparata con l’ipotesi più ampia: si conferma aprendo la sezione" className={`shrink-0 rounded-md bg-amber-100 font-medium text-amber-800 ${small ? 'px-1 py-0.5 text-[10px] leading-none' : 'px-1.5 py-0.5 text-xs'}`}>
      da confermare
    </span>
  );
}
